/**
 * PWA Service Worker Registration and Chunk Preloader
 * For LG WebOS 6+ and Samsung Tizen 4+
 * 
 * Features:
 * - Service worker registration with update handling
 * - Background chunk preloading for fast subsequent loads
 * - Offline notification handling
 * - Cache management
 */

import { isSmartTV, logDeviceInfo } from './deviceDetection';

// eslint-disable-next-line no-unused-vars
let serviceWorkerRegistration = null;

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
    // Development mode - still register but log for visibility
    if (process.env.NODE_ENV === 'development') {
        console.log('[PWA] Development mode - service worker enabled for testing');
    }

    if (!('serviceWorker' in navigator)) {
        console.warn('[PWA] Service Worker not supported');
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/',
            updateViaCache: 'none',
        });

        serviceWorkerRegistration = registration;
        console.log('[PWA] Service Worker registered:', registration.scope);

        // Handle updates
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            console.log('[PWA] Service Worker update found');

            newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    console.log('[PWA] New content available, refresh to update');
                    // Optionally notify user about update
                    notifyUpdate();
                }
            });
        });

        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        console.log('[PWA] Service Worker is ready');

        // Start chunk preloading for Smart TVs
        if (isSmartTV()) {
            setTimeout(() => {
                preloadAllChunks();
            }, 2000); // Delay to not block initial render
        }

        return registration;
    } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
        return null;
    }
}

/**
 * Unregister service worker
 */
export async function unregisterServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
        await registration.unregister();
    }
    console.log('[PWA] Service Workers unregistered');
}

/**
 * Send message to service worker
 * @param {Object} message - Message to send
 * @returns {Promise<any>} Response from service worker
 */
export function sendMessageToSW(message) {
    return new Promise((resolve, reject) => {
        if (!navigator.serviceWorker.controller) {
            reject(new Error('No service worker controller'));
            return;
        }

        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
            resolve(event.data);
        };

        navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2]);
    });
}

/**
 * Listen for messages from service worker
 * @param {Function} callback - Message handler
 */
export function onServiceWorkerMessage(callback) {
    if (!('serviceWorker' in navigator)) {
        return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
        callback(event.data);
    });
}

/**
 * Preload all JS and CSS chunks in background
 */
export async function preloadAllChunks() {
    console.log('[PWA] Starting chunk preload...');

    try {
        // Get list of all chunks from asset manifest and DOM
        const chunks = await discoverChunks();

        if (chunks.length === 0) {
            console.log('[PWA] No chunks to preload');
            return;
        }

        console.log('[PWA] Found', chunks.length, 'chunks to preload');

        // Send to service worker for caching
        if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'PRECACHE_CHUNKS',
                payload: { chunks }
            });
        } else {
            // Fallback: preload directly
            await preloadChunksDirect(chunks);
        }
    } catch (error) {
        console.error('[PWA] Chunk preload failed:', error);
    }
}

/**
 * Discover all JS and CSS chunks to preload
 * Uses asset-manifest.json for comprehensive coverage
 * @returns {Promise<string[]>} Array of chunk URLs
 */
async function discoverChunks() {
    const chunks = new Set();
    const baseUrl = window.location.origin;

    // Try to fetch asset manifest first (most comprehensive)
    try {
        const manifestResponse = await fetch('/asset-manifest.json');
        if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();

            // Add all files from manifest (except source maps)
            if (manifest.files) {
                Object.values(manifest.files).forEach((url) => {
                    if (!url.endsWith('.map')) {
                        chunks.add(url.startsWith('http') ? url : baseUrl + url);
                    }
                });
            }

            console.log('[PWA] Discovered', chunks.size, 'assets from manifest');
        }
    } catch (e) {
        console.log('[PWA] Could not fetch asset manifest, falling back to DOM discovery');
    }

    // Fallback: Get chunks from existing script tags
    document.querySelectorAll('script[src]').forEach((script) => {
        const src = script.getAttribute('src');
        if (src && (src.includes('/static/') || src.includes('chunk'))) {
            chunks.add(src.startsWith('http') ? src : baseUrl + src);
        }
    });

    // Get chunks from link tags
    document.querySelectorAll('link[href]').forEach((link) => {
        const href = link.getAttribute('href');
        if (href && (href.includes('/static/') || href.includes('chunk') || href.endsWith('.css'))) {
            chunks.add(href.startsWith('http') ? href : baseUrl + href);
        }
    });

    // Try to discover additional chunks from webpack manifest
    if (window.__WEBPACK_MANIFEST__) {
        Object.values(window.__WEBPACK_MANIFEST__).forEach((chunk) => {
            if (typeof chunk === 'string') {
                chunks.add(chunk.startsWith('http') ? chunk : baseUrl + chunk);
            }
        });
    }

    // Get preload/prefetch hints
    document.querySelectorAll('link[rel="preload"], link[rel="prefetch"]').forEach((link) => {
        const href = link.getAttribute('href');
        if (href) {
            chunks.add(href.startsWith('http') ? href : baseUrl + href);
        }
    });

    return Array.from(chunks);
}

/**
 * Directly preload chunks (fallback when SW not ready)
 * @param {string[]} chunks - Chunk URLs
 */
async function preloadChunksDirect(chunks) {
    let loaded = 0;

    const preloadChunk = async (url) => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'force-cache'
            });
            if (response.ok) {
                loaded++;
                notifyProgress(loaded, chunks.length);
            }
        } catch (e) {
            // Ignore preload errors
        }
    };

    // Preload in parallel batches of 5
    const batchSize = 5;
    for (let i = 0; i < chunks.length; i += batchSize) {
        const batch = chunks.slice(i, i + batchSize);
        await Promise.all(batch.map(preloadChunk));
    }

    console.log('[PWA] Preloaded', loaded, 'of', chunks.length, 'chunks');
}

/**
 * Notify about preload progress
 * @param {number} loaded - Loaded count
 * @param {number} total - Total count
 */
function notifyProgress(loaded, total) {
    const progress = Math.round((loaded / total) * 100);

    // Dispatch custom event for UI to listen
    window.dispatchEvent(new CustomEvent('pwa-preload-progress', {
        detail: { loaded, total, progress }
    }));
}

/**
 * Notify user about available update
 */
function notifyUpdate() {
    window.dispatchEvent(new CustomEvent('pwa-update-available'));
}

/**
 * Skip waiting and activate new service worker
 */
export function updateServiceWorker() {
    if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
}

/**
 * Get cache statistics
 * @returns {Promise<{usage: number, quota: number}>}
 */
export async function getCacheStats() {
    if (!navigator.storage || !navigator.storage.estimate) {
        return { usage: 0, quota: 0 };
    }

    const estimate = await navigator.storage.estimate();
    return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        usagePercent: ((estimate.usage || 0) / (estimate.quota || 1) * 100).toFixed(2)
    };
}

/**
 * Clear all cached data
 * Works even without service worker by directly using the Cache API
 */
export async function clearCache() {
    try {
        let clearedCount = 0;

        // Try to clear via service worker (if available)
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            try {
                await sendMessageToSW({ type: 'CLEAR_CACHE' });
                console.log('[PWA] Cleared cache via service worker');
            } catch (swError) {
                console.log('[PWA] No service worker controller, using direct cache API');
            }
        }

        // Clear from the client side using Cache API directly
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            console.log('[PWA] Found', cacheNames.length, 'caches to clear');
            await Promise.all(cacheNames.map(name => {
                console.log('[PWA] Deleting cache:', name);
                return caches.delete(name);
            }));
            clearedCount += cacheNames.length;
        }

        console.log('[PWA] Cache cleared, total caches deleted:', clearedCount);
        return true;
    } catch (error) {
        console.error('[PWA] Failed to clear cache:', error);
        return false;
    }
}

/**
 * Check if app is running from cache (offline)
 */
export function isOffline() {
    return !navigator.onLine;
}

/**
 * Listen for online/offline status changes
 * @param {Function} callback - Status change handler
 */
export function onNetworkStatusChange(callback) {
    window.addEventListener('online', () => callback(true));
    window.addEventListener('offline', () => callback(false));

    // Initial status
    callback(navigator.onLine);
}

/**
 * Initialize PWA features
 */
export async function initializePWA() {
    console.log('[PWA] Initializing...');

    logDeviceInfo();

    // Register service worker
    const registration = await registerServiceWorker();

    // Setup network status listeners
    onNetworkStatusChange((online) => {
        console.log('[PWA] Network status:', online ? 'online' : 'offline');
        window.dispatchEvent(new CustomEvent('pwa-network-status', {
            detail: { online }
        }));
    });

    // Listen for service worker messages
    onServiceWorkerMessage((message) => {
        console.log('[PWA] Message from SW:', message);

        switch (message.type) {
            case 'CHUNK_LOADED':
                notifyProgress(message.payload.loaded, message.payload.total);
                break;
            case 'PRECACHE_COMPLETE':
                console.log('[PWA] Precache complete:', message.payload);
                window.dispatchEvent(new CustomEvent('pwa-preload-complete', {
                    detail: message.payload
                }));
                break;
            default:
                // Unknown message type, ignore
                break;
        }
    });

    return registration;
}

/**
 * Force app update - clears all caches, unregisters service worker, and reloads
 * Used for forced updates when app version is below minimum required
 */
export async function forceAppUpdate() {
    console.log('[PWA] Force update triggered');

    try {
        // Clear all caches first
        await clearCache();
        console.log('[PWA] All caches cleared');

        // Unregister service worker to force fresh install
        await unregisterServiceWorker();
        console.log('[PWA] Service worker unregistered');

        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 500));

        // Hard reload to fetch new resources
        // Using location.href assignment for better TV compatibility
        window.location.href = window.location.origin + window.location.pathname + '?_=' + Date.now();
    } catch (error) {
        console.error('[PWA] Force update failed:', error);
        // Even if it fails, still reload
        window.location.reload(true);
    }
}

const pwaManager = {
    registerServiceWorker,
    unregisterServiceWorker,
    preloadAllChunks,
    updateServiceWorker,
    getCacheStats,
    clearCache,
    isOffline,
    onNetworkStatusChange,
    initializePWA,
    forceAppUpdate,
};

export default pwaManager;

